import React, {useRef} from 'react';
import T from 'prop-types';
import {Dialog} from '@material-ui/core';
import {Form, Formik} from 'formik';
import cn from 'classnames';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import DialogTitle from 'components/DialogTitle/styles';
import {DialogBody, FieldInput} from 'routes/App/dashboard/styles';
import DialogFields from 'components/DialogFields/styles';
import DialogField from 'components/DialogField/styles';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import DialogActions from 'components/DialogActions/styles';
import DialogAction from 'components/DialogAction/styles';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import {FieldLabel} from 'components/FieldLabel/styles';
import {DialogSubTitle} from 'components/DialogSubTitle/styles';
import SelfDeliveryReportEmailChangeSchema from 'helpers/services/formValidationSchemas/SelfDeliveryReportEmailChangeSchema';

const SelfDeliveryReportEmailChangeModal = ({
  open,
  currentEmail,
  isSubmitting,
  onClose,
  onSubmit
}) => {
  const formikRef = useRef();

  return (
    <Formik
      key={
        open
          ? 'self-delivery-email-change-open'
          : 'self-delivery-email-change-closed'
      }
      initialValues={{newEmail: ''}}
      enableReinitialize
      innerRef={formikRef}
      validationSchema={SelfDeliveryReportEmailChangeSchema}
      onSubmit={(values) => {
        onSubmit(values.newEmail.trim());
      }}
    >
      {({values, handleChange, handleBlur, errors, touched, submitForm}) => (
        <Dialog
          open={open}
          onClose={() => {
            if (!isSubmitting) {
              onClose();
            }
          }}
          scroll='body'
        >
          <DialogContainer className='DialogMdPadding'>
            <DialogClose
              onClick={() => {
                if (!isSubmitting) {
                  onClose();
                }
              }}
            />
            <DialogTitle className='DialogTitleMarginBottom'>
              Change self-delivery report email
            </DialogTitle>
            <DialogSubTitle className='textCenterBoldMarginBottomLarge'>
              Enter the new email address. It is updated immediately when a
              billing account exists. Otherwise Support is notified.
            </DialogSubTitle>
            <DialogBody>
              <Form>
                <DialogFields>
                  {currentEmail ? (
                    <DialogField className='w100'>
                      <FieldLabel className='FieldLabelLg modalLabel'>
                        Current email
                      </FieldLabel>
                      <FieldInput
                        value={currentEmail}
                        name='currentEmail'
                        disabled
                        readOnly
                        onChange={() => {}}
                      />
                    </DialogField>
                  ) : null}
                  <DialogField className='w100'>
                    <FieldLabel
                      className={cn('FieldLabelLg modalLabel', {
                        error: errors.newEmail && touched.newEmail && 'error'
                      })}
                    >
                      New email <span>*</span>
                    </FieldLabel>
                    <FieldInput
                      className={errors.newEmail && touched.newEmail && 'error'}
                      name='newEmail'
                      value={values.newEmail}
                      placeholder='New email address'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus
                      disabled={isSubmitting}
                    />
                    <ErrorSignWrapper className='positionRight2'>
                      {errors.newEmail && touched.newEmail && (
                        <ErrorSign>{errors.newEmail}</ErrorSign>
                      )}
                    </ErrorSignWrapper>
                  </DialogField>
                </DialogFields>
                <DialogActions className='mTop30'>
                  <DialogAction className='w50'>
                    <ButtonOutline
                      type='button'
                      className={cn({disabled: isSubmitting})}
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      <span>Cancel</span>
                    </ButtonOutline>
                  </DialogAction>
                  <DialogAction className='w50'>
                    <ButtonPrimary
                      type='button'
                      className={cn({disabled: isSubmitting})}
                      onClick={submitForm}
                      disabled={isSubmitting}
                    >
                      <span>Save</span>
                    </ButtonPrimary>
                  </DialogAction>
                </DialogActions>
              </Form>
            </DialogBody>
          </DialogContainer>
        </Dialog>
      )}
    </Formik>
  );
};

SelfDeliveryReportEmailChangeModal.propTypes = {
  open: T.bool.isRequired,
  currentEmail: T.string,
  isSubmitting: T.bool,
  onClose: T.func.isRequired,
  onSubmit: T.func.isRequired
};

SelfDeliveryReportEmailChangeModal.defaultProps = {
  currentEmail: '',
  isSubmitting: false
};

export default SelfDeliveryReportEmailChangeModal;
